import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { LogOut, User, Menu, X } from 'lucide-react';
import TokenMeter from './TokenMeter';

const Navbar = ({ onSidebarToggle, showSidebarToggle = false }) => {
  const { user, logout, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/login');
    setIsMenuOpen(false);
  };

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const isOnChatPage = location.pathname === '/chat';

  return (
    <nav className="bg-white shadow-sm border-b border-gray-200 sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          <div className="flex items-center space-x-3">
            {/* Sidebar Toggle for Chat Page */}
            {isOnChatPage && isAuthenticated && (
              <button
                onClick={onSidebarToggle}
                className="lg:hidden p-2 hover:bg-gray-100 rounded-md transition-colors"
              >
                <Menu className="w-5 h-5 text-gray-600" />
              </button>
            )}
            
            <Link to="/" className="flex items-center space-x-2">
              <div className="w-8 h-8 bg-gradient-to-r from-primary-500 to-primary-600 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-sm">AI</span>
              </div>
              <span className="text-lg sm:text-xl font-bold text-gray-900 hidden xs:block">Fusion Chat</span>
            </Link>
          </div>

          {isAuthenticated ? (
            <>
              <div className="flex items-center space-x-2 md:space-x-4 lg:space-x-6">
                {/* Token Meter */}
                <TokenMeter tokens={user?.tokens || 0} />
                
                {/* Desktop Menu */}
                <div className="hidden md:flex items-center space-x-3">
                  <Link
                    to="/profile"
                    className="flex items-center space-x-2 text-gray-700 hover:text-primary-600 transition-colors"
                  >
                    <User className="w-4 h-4" />
                    <span className="text-sm font-medium hidden lg:block">{user?.name}</span>
                  </Link>
                  
                  <button
                    onClick={handleLogout}
                    className="flex items-center space-x-2 text-gray-700 hover:text-red-600 transition-colors"
                  >
                    <LogOut className="w-4 h-4" />
                    <span className="text-sm font-medium hidden lg:block">Logout</span>
                  </button>
                </div>

                {/* Mobile Menu Button */}
                <div className="md:hidden">
                  <button
                    onClick={toggleMenu}
                    className="p-2 text-gray-700 hover:text-primary-600 transition-colors"
                  >
                    {isMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
                  </button>
                </div>
              </div>
            </>
          ) : (
            <div className="flex items-center space-x-4">
              <Link
                to="/login"
                className="text-gray-700 hover:text-primary-600 font-medium transition-colors"
              >
                Login
              </Link>
              <Link
                to="/signup"
                className="btn-primary"
              >
                Sign Up
              </Link>
            </div>
          )}
        </div>

        {isAuthenticated && isMenuOpen && (
          <div className="md:hidden border-t border-gray-200 py-4">
            <div className="flex flex-col space-y-4 px-4">
              {/* Mobile Token Display */}
              <div className="bg-gray-50 p-3 rounded-lg">
                <TokenMeter tokens={user?.tokens || 0} className="justify-center" />
              </div>
              
              <Link
                to="/profile"
                onClick={() => setIsMenuOpen(false)}
                className="flex items-center space-x-3 text-gray-700 hover:text-primary-600 transition-colors py-3 px-2 rounded-lg hover:bg-gray-50"
              >
                <User className="w-5 h-5" />
                <div className="flex-1">
                  <span className="text-sm font-medium block">{user?.name}</span>
                  <span className="text-xs text-gray-500">View Profile</span>
                </div>
              </Link>
              
              <button
                onClick={handleLogout}
                className="flex items-center space-x-3 text-red-600 hover:text-red-700 transition-colors py-3 px-2 rounded-lg hover:bg-red-50"
              >
                <LogOut className="w-5 h-5" />
                <span className="text-sm font-medium">Logout</span>
              </button>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
